import {
    FormLabel,
    LandmarkIcon,
    LinearProgress,
    IdOptionType,
    SelectButtonGroup,
    SelectList,
    ModalContent,
    modalContext,
    SearchableSelect,
    OptionType
} from "shared";
import {
    IBusinessGroup,
    ITemplateYandexComponent,
    YandexListTypes,
    IOrganization,
    templatesService,
    IBankDetail
} from "entities/template";
import React, { ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { useComponentsTranslations } from "../../../../lib/i18n";
import { BankDetailsModal } from "./bank_details_modal/bank_details_modal";
import styles from "./company_list.module.scss";
import { useParams } from "react-router-dom";

interface ICompanyListProps {
    component: ITemplateYandexComponent
    onChange: (value: Partial<ITemplateYandexComponent>) => void

}

export const CompanyList: React.FC<ICompanyListProps> = React.memo(({
                                                                        component,
                                                                        onChange
                                                                    }) => {
    const t = useComponentsTranslations();
    const { close, openModal } = useContext(modalContext);
    const [businessGroups, setBusinessGroups] = useState<IBusinessGroup[]>([]);
    const [organizations, setOrganizations] = useState<IOrganization[]>([]);
    const [buttonGroup, setButtonGroup] = useState<ReactNode>();
    const [options, setOptions] = useState<IdOptionType[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [previousComponentSettings, setPreviousComponentSettings] = useState<ITemplateYandexComponent>();
    const { extUserId } = useParams();

    useEffect(() => {
        if (!component.optionsList) {
            onChange({
                optionsList: YandexListTypes.yaOrgs
            })
        }
    }, [component])

    const organizationOptionMap = (o: IOrganization) => {
        return {
            value: o.ya_name,
            label: o.ya_name,
            id: o.id
        }
    }

    const bgOptionMap = (bg: IBusinessGroup) => {
        return {
            value: bg.name,
            label: bg.name,
            id: bg.id
        }
    }

    useEffect(() => {
        setLoading(true);
        const promises = [templatesService.loadBusinessGroups(extUserId), templatesService.loadOrganizations(extUserId)]
        Promise.all(promises).then(([groups, organizations]) => {
            setLoading(false);
            setBusinessGroups(groups as IBusinessGroup[]);
            setOrganizations(organizations as IOrganization[]);
        })
    }, []);

    useEffect(() => {
        if (businessGroups.length > 0 && organizations.length > 0) {
            const defaultValue = getDefaultValue(component.optionsList, businessGroups, organizations);
            if (!component.list) {
                onChange({
                    list: [defaultValue]
                });
            } else {
                if (component.optionsList === YandexListTypes.yaOrgs) {
                    // проверяем на несуществующие названия компаний
                    const orgNameMap = organizations.map(o => o.ya_name);
                    const cleanCompanyList = component.list.filter(companyName => orgNameMap.includes(companyName));
                    if (cleanCompanyList.length === 0) {
                        onChange({
                            list: [defaultValue]
                        });
                    } else if (cleanCompanyList.length !== component.list.length) {
                        onChange({
                            list: cleanCompanyList
                        });
                    }
                }
                if (component.optionsList === YandexListTypes.yaBGs) {
                    const cleanBusinessGroups = businessGroups.filter(bg => component.list?.includes(bg.name));
                    if (cleanBusinessGroups.length === 0) {
                        onChange({
                            list: [defaultValue]
                        });
                    }
                }
            }
        }
    }, [component.optionsList, businessGroups, organizations])

    useEffect(() => {
        if (businessGroups.length > 0 && organizations.length > 0) {
            switch (component.optionsList) {
                case YandexListTypes.yaOrgs:
                    setOptions(organizations
                        .sort((a, b) => a.ya_name.localeCompare(b.ya_name))
                        .map(organizationOptionMap));
                    break;
                case YandexListTypes.yaBGs:
                    setOptions(businessGroups
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map(bgOptionMap));
                    break;
                default:
                    setOptions([]);
            }

        }
    }, [component.optionsList, businessGroups, organizations])

    const onRenderButtonGroup = (node: ReactNode) => {
        setButtonGroup(node);
    }

    const defaultValueOptionsConstructor = (optionsList: YandexListTypes, orgs: IOrganization[], bgs: IBusinessGroup[], list?: string[]) => {
        let options: OptionType[] = [];
        switch (optionsList) {
            case YandexListTypes.yaOrgs:
                options = orgs.filter(o => list ? list.includes(o.ya_name) : false)
                    .map(organizationOptionMap)
                break;
            case YandexListTypes.yaBGs:
                const bgIds = bgs.filter((bg) => list ? list.includes(bg.name) : false).map((bg) => bg.id);
                options = orgs.filter(o => bgIds.includes(o.ya_bg_id))
                    .map(organizationOptionMap)
                break;
            default:
        }

        return options;
    }

    const defaultValueOptions = useMemo(() => {
        return defaultValueOptionsConstructor(component.optionsList, organizations, businessGroups, component.list)
    }, [component.optionsList, component.list, organizations, businessGroups])

    const getDefaultValue = (type: YandexListTypes, groups: IBusinessGroup[], orgs: IOrganization[]) => {
        let defaultValue = "";
        switch (type) {
            case YandexListTypes.yaOrgs:
                const defaultOrganization = orgs.find((o) => o.isDefault === "1");
                if (defaultOrganization) {
                    defaultValue = defaultOrganization.ya_name
                }
                break;
            case YandexListTypes.yaBGs:
                const defaultBusinessGroup = groups.find((bg) => bg.id === 0);
                if (defaultBusinessGroup) {
                    defaultValue = defaultBusinessGroup.name
                }
                break;
            default:
                break;
        }
        return defaultValue;
    }

    const onOptionsListChange = (value: YandexListTypes) => {
        const changes: Partial<ITemplateYandexComponent> = {
            optionsList: value,
            list: previousComponentSettings ? previousComponentSettings.list : [getDefaultValue(value, businessGroups, organizations)],
            defaultValue: previousComponentSettings ? previousComponentSettings.defaultValue : undefined
        };
        setPreviousComponentSettings({ ...component });
        onChange(changes);
    }

    const onSelectListChange = (value: string[]) => {
        const listBankDetails: IBankDetail[] = [];
        let defaultValue: string | undefined = undefined;
        const defaultValueOpts = defaultValueOptionsConstructor(component.optionsList, organizations, businessGroups, value);

        if (component.listBankDetails) {
            component.listBankDetails.forEach((detail) => {
                listBankDetails.push({ ...detail });
            })
        }

        if (component.defaultValue && defaultValueOpts.map(o => o.value).includes(component.defaultValue)) {
            defaultValue = component.defaultValue;
        }

        onChange({
            list: value,
            listBankDetails: listBankDetails.length > 0 ? listBankDetails : undefined,
            defaultValue
        });
    }

    const renderActionButton = () => {
        return component.optionsList === YandexListTypes.yaOrgs ? <LandmarkIcon/> : null;
    }

    const onBankDetailsSave = (value: IBankDetail) => {
        const updatedListBankDetails = component.listBankDetails ? [...component.listBankDetails] : [];
        const replacedIndex = updatedListBankDetails.findIndex((detail) => detail.item === value.item);
        if (replacedIndex !== -1) {
            updatedListBankDetails[replacedIndex] = value;
        } else {
            updatedListBankDetails.push(value);
        }
        onChange({
            listBankDetails: updatedListBankDetails
        });
    }

    const openBankDetailsModal = (index: number) => {
        const companyName = component.list ? component.list[index] : undefined;
        let values: number[] = [];
        let defaultValue: number | undefined = undefined;
        if (component.listBankDetails) {
            const bankDetails = component.listBankDetails.find((d) => d.item === companyName);
            if (bankDetails) {
                if (bankDetails.bankNameWhiteList && bankDetails.bankNameWhiteList.length > 0) {
                    values = bankDetails.bankNameWhiteList;
                }
                defaultValue = bankDetails.bankNameDefaultValue
            }
        }

        if (companyName) {
            openModal(<ModalContent close={close}
                                    title={t("addBankDetails")}
                                    subtitle={companyName}
                                    showButtonGroup={false}>
                <BankDetailsModal close={close} companyName={companyName} values={values} defaultValue={defaultValue}
                                  onSave={onBankDetailsSave}/>
            </ModalContent>);
        }
    }

    const radioOptions = [
        {
            value: YandexListTypes.yaOrgs,
            content: t("companies")
        },
        {
            value: YandexListTypes.yaBGs,
            content: t("countries")
        },
    ]


    return <div className={styles.container}>
        <FormLabel title={t("list")}>
            {!loading && <>
                <div className={styles.listActionButtons}>
                    {buttonGroup}
                </div>
                <SelectButtonGroup
                    className={styles.radioGroup}
                    options={radioOptions}
                    selectedValue={component.optionsList}
                    onChange={(val) => onOptionsListChange(val as YandexListTypes)}
                />
                <SelectList
                    value={component.list}
                    actionButton={renderActionButton()}
                    options={options}
                    resetFactor={component.id}
                    renderButtonGroup={onRenderButtonGroup}
                    onActionButtonClick={openBankDetailsModal}
                    onChange={onSelectListChange}/>
                <FormLabel title={t("defaultValue")} className={styles.defaultValueSelect}>
                    <SearchableSelect
                        options={defaultValueOptions}
                        optionValue={component.defaultValue ? defaultValueOptions.find((o) => o.value === component.defaultValue) as OptionType : undefined}
                        onChange={(val) => {
                            const defaultValue = (val ? val as string : undefined);
                            onChange({ defaultValue })
                        }}
                        isMulti={false}
                        hideClearIcon={false}
                        isSearchable={true}
                    />
                </FormLabel>
            </>}

            {loading && <LinearProgress className={styles.progressBar}/>}
        </FormLabel>
    </div>
});
